import { supabase } from '@/lib/supabase';
import { unifiedStartupCache, UserPreferencesData } from './unifiedStartupCache';
import { createScopedLogger } from '@/utils/logging/createScopedLogger';

const logger = createScopedLogger('UserPreferences');
const logWithTimestamp = (msg: string, ...args: unknown[]) => logger.info(msg, ...args);
const errorWithTimestamp = (msg: string, ...args: unknown[]) => logger.error(msg, ...args);

export interface UserPreferences {
  interfaceLanguage: string | null;
  subtitleLanguage: string | null;
  isPaying: boolean;
}

class UserPreferencesService {
  private isLoading = false;
  private loadPromise: Promise<UserPreferences | null> | null = null;

  async loadAllPreferences(smartuserId: string): Promise<UserPreferences | null> {
    if (!smartuserId) {
      errorWithTimestamp('[UserPrefs] Cannot load preferences: no smartuserId provided');
      return null;
    }

    const cachedPrefs = unifiedStartupCache.getUserPreferences(smartuserId);
    if (cachedPrefs) {
      logWithTimestamp(`[UserPrefs] Using cached preferences for user ${smartuserId}`);
      return {
        interfaceLanguage: cachedPrefs.interfaceLanguage,
        subtitleLanguage: cachedPrefs.subtitleLanguage,
        isPaying: cachedPrefs.isPaying,
      };
    }

    if (this.isLoading && this.loadPromise) {
      logWithTimestamp('[UserPrefs] Load already in progress, waiting...');
      return this.loadPromise;
    }

    this.isLoading = true;
    this.loadPromise = this.fetchAndCachePreferences(smartuserId);

    try {
      const result = await this.loadPromise;
      return result;
    } finally {
      this.isLoading = false;
      this.loadPromise = null;
    }
  }

  private async fetchAndCachePreferences(smartuserId: string): Promise<UserPreferences | null> {
    const startTime = Date.now();
    logWithTimestamp(`[UserPrefs] Fetching all preferences for user ${smartuserId}...`);

    try {
      const { data, error } = await supabase
        .from('users')
        .select('interface_preferred_language, subtitle_preferred_language, is_paying')
        .eq('smartuser_id', smartuserId)
        .maybeSingle();

      const duration = Date.now() - startTime;

      if (error) {
        errorWithTimestamp(`[UserPrefs] Error fetching preferences (${duration}ms):`, error);
        return null;
      }

      if (!data) {
        logWithTimestamp(`[UserPrefs] No user data found for ${smartuserId} (${duration}ms)`);
        return null;
      }

      const preferences: UserPreferences = {
        interfaceLanguage: data.interface_preferred_language || null,
        subtitleLanguage: data.subtitle_preferred_language || null,
        isPaying: data.is_paying === true,
      };

      logWithTimestamp(
        `[UserPrefs] Loaded preferences in ${duration}ms:`,
        `lang=${preferences.interfaceLanguage || 'none'},`,
        `subtitle=${preferences.subtitleLanguage || 'none'},`,
        `isPaying=${preferences.isPaying}`
      );

      await unifiedStartupCache.updateUserPreferences(smartuserId, preferences);

      return preferences;
    } catch (error) {
      errorWithTimestamp('[UserPrefs] Exception fetching preferences:', error);
      return null;
    }
  }

  getInterfaceLanguage(smartuserId: string): string | null {
    const prefs = unifiedStartupCache.getUserPreferences(smartuserId);
    return prefs?.interfaceLanguage || null;
  }

  getSubtitleLanguage(smartuserId: string): string | null {
    const prefs = unifiedStartupCache.getUserPreferences(smartuserId);
    return prefs?.subtitleLanguage || null;
  }

  getIsPaying(smartuserId: string): boolean | null {
    const prefs = unifiedStartupCache.getUserPreferences(smartuserId);
    if (!prefs) return null;
    return prefs.isPaying;
  }

  isLoaded(smartuserId: string): boolean {
    return unifiedStartupCache.hasUserPreferences(smartuserId);
  }

  async updateInterfaceLanguage(smartuserId: string, language: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('users')
        .update({ interface_preferred_language: language })
        .eq('smartuser_id', smartuserId);

      if (error) {
        errorWithTimestamp('[UserPrefs] Error updating interface language:', error);
        return false;
      }

      const currentPrefs = unifiedStartupCache.getUserPreferences(smartuserId);
      await unifiedStartupCache.updateUserPreferences(smartuserId, {
        interfaceLanguage: language,
        subtitleLanguage: currentPrefs?.subtitleLanguage || null,
        isPaying: currentPrefs?.isPaying || false,
      });

      logWithTimestamp(`[UserPrefs] Interface language updated to ${language}`);
      return true;
    } catch (error) {
      errorWithTimestamp('[UserPrefs] Exception updating interface language:', error);
      return false;
    }
  }

  async updateSubtitleLanguage(smartuserId: string, language: string | null): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('users')
        .update({ subtitle_preferred_language: language })
        .eq('smartuser_id', smartuserId);

      if (error) {
        errorWithTimestamp('[UserPrefs] Error updating subtitle language:', error);
        return false;
      }

      const currentPrefs = unifiedStartupCache.getUserPreferences(smartuserId);
      await unifiedStartupCache.updateUserPreferences(smartuserId, {
        interfaceLanguage: currentPrefs?.interfaceLanguage || null,
        subtitleLanguage: language,
        isPaying: currentPrefs?.isPaying || false,
      });

      logWithTimestamp(`[UserPrefs] Subtitle language updated to ${language || 'disabled'}`);
      return true;
    } catch (error) {
      errorWithTimestamp('[UserPrefs] Exception updating subtitle language:', error);
      return false;
    }
  }

  async updateIsPaying(smartuserId: string, isPaying: boolean): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('users')
        .update({ is_paying: isPaying, updated_at: new Date().toISOString() })
        .eq('smartuser_id', smartuserId);

      if (error) {
        errorWithTimestamp('[UserPrefs] Error updating is_paying:', error);
        return false;
      }

      const currentPrefs = unifiedStartupCache.getUserPreferences(smartuserId);
      await unifiedStartupCache.updateUserPreferences(smartuserId, {
        interfaceLanguage: currentPrefs?.interfaceLanguage || null,
        subtitleLanguage: currentPrefs?.subtitleLanguage || null,
        isPaying,
      });

      logWithTimestamp(`[UserPrefs] isPaying updated to ${isPaying}`);
      return true;
    } catch (error) {
      errorWithTimestamp('[UserPrefs] Exception updating isPaying:', error);
      return false;
    }
  }

  async clear(): Promise<void> {
    await unifiedStartupCache.clearUserPreferences();
    logWithTimestamp('[UserPrefs] Preferences cleared');
  }
}

export const userPreferencesService = new UserPreferencesService();
